import React from 'react';
import { formatRate } from '../format-rate';
import { TrendRow } from '~analytics-components/kpi-tile';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import { BoxedTileRow } from './boxed-tile-row';
import { figureTile } from './tile-figure';
import type { KpiFormProps } from '../types';
import style from './rate-form.module.scss';

/** The prominent percentage on its own, sliding toward its new figure on a data
 * change; a null rate stays `N/A`, untweened. Shared with the columns headline,
 * which reuses this form's idiom and stylesheet. */
export const RateValue: React.FC<{ rate: number | null }> = ({ rate }) => {
    const tweenedRate = useTweenedValue(rate ?? 0);

    return (
        <span className={`${style.rate_value} h3_medium`}>
            {formatRate(rate == null ? null : tweenedRate)}
        </span>
    );
};

/** Rate form: the percentage prominent, with an optional trend row beneath.
 * `boxed` renders the rate inside a single highlighted `BoxedTileRow` cell,
 * tweened and formatted by `KpiTile` like the `trio` tiles, instead of flat. */
export const RateForm: React.FC<KpiFormProps> = ({
    data,
    invertTrend,
    boxed,
}) => {
    if (data.form !== 'rate') return null;
    if (boxed) {
        return (
            <BoxedTileRow
                highlightIndex={0}
                tiles={[
                    {
                        ...figureTile(data.rate, { isPercent: true }),
                        trend: data.trend,
                        invertTrend,
                    },
                ]}
            />
        );
    }
    return (
        <div className={style.rate_form}>
            <RateValue rate={data.rate} />
            {data.trend && <TrendRow trend={data.trend} invert={invertTrend} />}
        </div>
    );
};
